import React, {ReactElement, useContext, useMemo} from 'react';
import ConsolidatedLoansTableRow from './ConsolidatedLoansTableRow';
import {ConsolidatedLoansContext} from '../consolidated-loans-context';
import isEmpty from 'lodash/isEmpty';
import BigNumber from 'bignumber.js';
import {CBSLoanDetails} from '../../../../../../../shared/model/cbs-loans';
import {WaiveInputContext} from '../waive/waive-input-context';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import {WaiveType} from '../../../../../../../shared/model/consolidate-loans';

const ConsolidatedLoansTableFooter = (): ReactElement => {

  const {consolidatedLoans} = useContext(ConsolidatedLoansContext);
  const {waiveInputs} = useContext(WaiveInputContext);

  const groupedWaiveAmount = useMemo(() => {
    const grouped = groupBy(waiveInputs, 'waiveType');
    return mapValues(grouped, items => {
    const totalWaiveAmount = items.reduce((acc, item) => {
      return acc.plus(new BigNumber(item.waiveAmount));
    }, new BigNumber(0));
    return totalWaiveAmount.toNumber();
  });
  }, [waiveInputs]);

  const getTotalLoanValue = (): CBSLoanDetails => {
    const initialValue = {
      loanId: 0, // For initial value only and will not be used as an input
      customerName: 'Total:',
      principalBalance: 0.00,
      interestBalance: 0.00,
      cbuAmount: 0.00,
      pfAmount: 0.00,
      tpAmount: 0.00,
      pastDueInterestAmount: 0.00,
      penaltiesAmount: 0.00,
      customFeesAmount: 0.00
    };

    if (isEmpty(consolidatedLoans)) {
      return initialValue;
    }

    const interestTotalWaiveAmount = groupedWaiveAmount[WaiveType.INTEREST];
    const cbuTotalWaiveAmount = groupedWaiveAmount[WaiveType.CBU];
    const pfTotalWaiveAmount = groupedWaiveAmount[WaiveType.PF];
    const tpTotalWaiveAmount = groupedWaiveAmount[WaiveType.TP];
    const pastDueInterestTotalWaiveAmount = groupedWaiveAmount[WaiveType.PAST_DUE_INTEREST];
    const penaltyTotalWaiveAmount = groupedWaiveAmount[WaiveType.PENALTY];
    const customFeeTotalWaiveAmount = groupedWaiveAmount[WaiveType.CUSTOM_FEE];

    const cbsLoanDetails = consolidatedLoans.reduce((item1, item2) => {
      item1.principalBalance = BigNumber(item1.principalBalance).plus(item2?.principalBalance || 0).toNumber();
      item1.interestBalance = BigNumber(item1.interestBalance).plus(item2?.interestBalance || 0).toNumber();
      item1.cbuAmount = BigNumber(item1.cbuAmount).plus(item2.cbuAmount).toNumber();
      item1.pfAmount = BigNumber(item1.pfAmount).plus(item2.pfAmount).toNumber();
      item1.tpAmount = BigNumber(item1.tpAmount).plus(item2.tpAmount).toNumber();
      item1.pastDueInterestAmount = BigNumber(item1.pastDueInterestAmount).plus(item2.pastDueInterestAmount).toNumber();
      item1.penaltiesAmount = BigNumber(item1.penaltiesAmount).plus(item2.penaltiesAmount).toNumber();
      item1.customFeesAmount = BigNumber(item1.customFeesAmount).plus(item2.customFeesAmount).toNumber();
      return item1;
    }, initialValue);
    
    if (!isEmpty(groupedWaiveAmount)) {
      return {
        loanId: initialValue.loanId,
        customerName: initialValue.customerName,
        principalBalance: cbsLoanDetails.principalBalance,
        interestBalance: BigNumber(cbsLoanDetails.interestBalance).minus(interestTotalWaiveAmount || 0).toNumber(),
        cbuAmount: BigNumber(cbsLoanDetails.cbuAmount).minus(cbuTotalWaiveAmount || 0).toNumber(),
        pfAmount: BigNumber(cbsLoanDetails.pfAmount).minus(pfTotalWaiveAmount || 0).toNumber(),
        tpAmount: BigNumber(cbsLoanDetails.tpAmount).minus(tpTotalWaiveAmount || 0).toNumber(),
        pastDueInterestAmount: BigNumber(cbsLoanDetails.pastDueInterestAmount).minus(pastDueInterestTotalWaiveAmount || 0).toNumber(),
        penaltiesAmount: BigNumber(cbsLoanDetails.penaltiesAmount).minus(penaltyTotalWaiveAmount || 0).toNumber(),
        customFeesAmount: BigNumber(cbsLoanDetails.customFeesAmount).minus(customFeeTotalWaiveAmount || 0).toNumber(),
      }
    }

    return cbsLoanDetails;
  };

  return <ConsolidatedLoansTableRow cbsLoanDetails={getTotalLoanValue()} />;

};

export default ConsolidatedLoansTableFooter;
